export const TICKET_STATUSES = [
  'NO_VIDEO',
  'PRONTA',
  'CHAMADA',
  'EM_ATENDIMENTO',
  'CONCLUIDO',
  'AUSENTE',
  'DESISTENTE',
] as const;

export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const ACTIVE_TICKET_STATUSES: readonly TicketStatus[] = ['CHAMADA', 'EM_ATENDIMENTO'];

export const ABANDONABLE_TICKET_STATUSES: readonly TicketStatus[] = [
  'NO_VIDEO',
  'PRONTA',
  'CHAMADA',
  'EM_ATENDIMENTO',
  'AUSENTE',
];

export const TICKET_STATUS_LABEL_KEYS: Record<TicketStatus, string> = {
  NO_VIDEO: 'SchedulingDeskStatusNoVideo',
  PRONTA: 'SchedulingDeskStatusReady',
  CHAMADA: 'SchedulingDeskStatusCalled',
  EM_ATENDIMENTO: 'SchedulingDeskStatusInService',
  CONCLUIDO: 'SchedulingDeskStatusDone',
  AUSENTE: 'SchedulingDeskStatusAbsent',
  DESISTENTE: 'SchedulingDeskStatusAbandoned',
};

export const MOVE_ACTIONS = ['READY', 'RETURN', 'RECALL', 'NO_SHOW', 'START', 'FINISH', 'ABANDON', 'RESUME'] as const;

export type MoveAction = (typeof MOVE_ACTIONS)[number];

export const NO_SHOW_MIN_CALLS = 3;

export const QUEUE_ERROR_CODES = [
  'APPOINTMENT_NOT_FOUND',
  'APPOINTMENT_NOT_ACTIVE',
  'APPOINTMENT_NOT_TODAY',
  'ALREADY_CHECKED_IN',
  'QUEUE_NOT_FOUND',
  'SERVICE_POINT_NOT_FOUND',
  'TICKET_NOT_FOUND',
  'INVALID_TRANSITION',
  'NO_SHOW_TOO_EARLY',
  'NOT_YOUR_TICKET',
  'TICKET_CHANGED',
  'TICKET_IN_PROGRESS',
  'COUNTER_NOT_FOUND',
  'COUNTER_BUSY',
  'ATTENDANT_HAS_SESSION',
  'NO_SESSION',
] as const;

export type QueueErrorCode = (typeof QUEUE_ERROR_CODES)[number];

const DESK_ERROR_KEYS: Partial<Record<QueueErrorCode, string>> = {
  NO_SESSION: 'SchedulingDeskErrNoSession',
  COUNTER_BUSY: 'SchedulingDeskErrCounterBusy',
  ATTENDANT_HAS_SESSION: 'SchedulingDeskErrAttendantHasSession',
  COUNTER_NOT_FOUND: 'SchedulingDeskErrCounterNotFound',
  TICKET_NOT_FOUND: 'SchedulingDeskErrTicketChanged',
  TICKET_CHANGED: 'SchedulingDeskErrTicketChanged',
  INVALID_TRANSITION: 'SchedulingDeskErrInvalidTransition',
  TICKET_IN_PROGRESS: 'SchedulingDeskErrTicketInProgress',
  NOT_YOUR_TICKET: 'SchedulingDeskErrNotYourTicket',
  NO_SHOW_TOO_EARLY: 'SchedulingDeskErrNoShowTooEarly',
};

export const DESK_ERROR_GENERIC_KEY = 'SchedulingDeskErrGeneric';

const KEEP_STATE_ON_ERROR: readonly QueueErrorCode[] = ['NO_SHOW_TOO_EARLY'];

export interface TicketQueue {
  id: string;
  name?: string;
  color?: string;
  ticket_letter?: string;
}

export interface TicketCounter {
  id: string;
  number: number;
  name: string;
}

export interface TicketView {
  id: string;
  code: string;
  status: TicketStatus;
  resume_status: TicketStatus | null;
  service_day: string;
  queue: TicketQueue;
  counter: TicketCounter | null;
  attendant_id: string | null;
  call_count: number;
  campaign_id: string;
  appointment: { id: string; code: string | null } | null;
  user_id: string;
  holder_name: string | null;
  arrived_at: string;
  ready_at: string | null;
  called_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface SessionView {
  id: string;
  counter_id: string;
  service_point_id: string;
  attendant_id: string;
  opened_at: string;
  closed_at: string | null;
}

export interface DeskCounter {
  id: string;
  number: number;
  name: string;
  busy: boolean;
  mine: boolean;
}

export interface CountersResponse {
  day: string;
  service_point: { id: string; name: string; timezone: string };
  counters: DeskCounter[];
}

export interface QueueListResponse {
  day: string;
  tickets: TicketView[];
  my_session: SessionView | null;
}

export const SMS_RESULTS = ['SENT', 'DISABLED', 'NO_PHONE', 'FAILED'] as const;

export type SmsResult = (typeof SMS_RESULTS)[number];

export interface ArriveResponse {
  ticket: TicketView;
  sms: SmsResult;
}

/** Resultado do check-in; `already` = a pessoa já tinha senha e a mesma senha voltou (409 ALREADY_CHECKED_IN). */
export interface CheckInResult {
  ticket: TicketView;
  sms: SmsResult | null;
  already: boolean;
}

/** Fila do ponto como a triagem a escolhe; lida direto do Supabase (RLS do vínculo no ponto). */
export interface PointQueue {
  id: string;
  name: string;
  color: string;
  ticket_letter: string;
  priority_order: number;
}

export interface CallNextResponse {
  ticket: TicketView | null;
}

export interface MoveResponse {
  ticket: TicketView;
}

export interface SessionResponse {
  session: SessionView;
}

export function isActiveTicket(ticket: Pick<TicketView, 'status'>): boolean {
  return ACTIVE_TICKET_STATUSES.includes(ticket.status);
}

export function myActiveTicket(tickets: readonly TicketView[], userId: string): TicketView | null {
  return tickets.find((ticket) => ticket.attendant_id === userId && isActiveTicket(ticket)) ?? null;
}

export function canRecall(ticket: Pick<TicketView, 'status'>): boolean {
  return ticket.status === 'CHAMADA';
}

export function canStart(ticket: Pick<TicketView, 'status'>): boolean {
  return ticket.status === 'CHAMADA';
}

export function canFinish(ticket: Pick<TicketView, 'status'>): boolean {
  return ticket.status === 'EM_ATENDIMENTO';
}

export function canMarkAbsent(ticket: Pick<TicketView, 'status' | 'call_count'>): boolean {
  return ticket.status === 'CHAMADA' && ticket.call_count >= NO_SHOW_MIN_CALLS;
}

export function canAbandon(ticket: Pick<TicketView, 'status'>): boolean {
  return ABANDONABLE_TICKET_STATUSES.includes(ticket.status);
}

export function canResume(ticket: Pick<TicketView, 'status'>): boolean {
  return ticket.status === 'DESISTENTE';
}

export function callsUntilAbsent(ticket: Pick<TicketView, 'call_count'>): number {
  return Math.max(NO_SHOW_MIN_CALLS - ticket.call_count, 0);
}

export function deskErrorKey(code: QueueErrorCode | null): string {
  return (code && DESK_ERROR_KEYS[code]) || DESK_ERROR_GENERIC_KEY;
}

export function shouldReloadAfter(code: QueueErrorCode | null): boolean {
  return code !== null && !KEEP_STATE_ON_ERROR.includes(code);
}

const TRIAGE_ERROR_KEYS: Partial<Record<QueueErrorCode, string>> = {
  APPOINTMENT_NOT_FOUND: 'SchedulingTriageErrAppointmentNotFound',
  APPOINTMENT_NOT_ACTIVE: 'SchedulingTriageErrNotActive',
  APPOINTMENT_NOT_TODAY: 'SchedulingTriageErrNotToday',
  QUEUE_NOT_FOUND: 'SchedulingTriageErrQueueNotFound',
  TICKET_NOT_FOUND: 'SchedulingTriageErrTicketChanged',
  TICKET_CHANGED: 'SchedulingTriageErrTicketChanged',
  INVALID_TRANSITION: 'SchedulingTriageErrTicketChanged',
};

export const TRIAGE_ERROR_GENERIC_KEY = 'SchedulingTriageErrGeneric';

export const TRIAGE_ERROR_NO_ACCESS_KEY = 'SchedulingTriageErrNoAccess';

export function triageErrorKey(code: QueueErrorCode | null): string {
  return (code && TRIAGE_ERROR_KEYS[code]) || TRIAGE_ERROR_GENERIC_KEY;
}
